/**
 * Shop mega — hover / focus for sidebar flyout and desktop slide-out panel.
 * Event delegation so the first defer script still binds after both header and
 * sidebar markup exist, and so a missing trigger at parse time cannot no-op.
 */
(() => {
  if (window.__geMegaBound) return;
  window.__geMegaBound = true;

  let closeTimer = null;
  let openPair = null;

  const isHorizontalNav = () => document.body.classList.contains('ge-nav-horizontal');

  const isDesktopOverlayPanel = (panel) =>
    panel.classList.contains('ge-mega--horizontal') || panel.classList.contains('ge-mega--slideout');

  const backdropEl = () => document.querySelector('[data-shop-mega-backdrop]');

  const pairFromTrigger = (trigger) => {
    if (!(trigger instanceof Element)) return null;
    if (!trigger.hasAttribute('data-shop-mega-trigger')) return null;
    const id = trigger.getAttribute('aria-controls');
    const panel = id ? document.getElementById(id) : null;
    if (!panel) return null;
    const overlay = isDesktopOverlayPanel(panel);
    if (isHorizontalNav()) return overlay ? { trigger, panel } : null;
    return overlay ? null : { trigger, panel };
  };

  const pairFromNode = (node) => {
    if (!(node instanceof Element)) return null;
    const trigger = node.closest('[data-shop-mega-trigger]');
    if (trigger) return pairFromTrigger(trigger);
    const panel = node.closest('[data-shop-mega-panel]');
    if (!panel) return null;
    const triggerEl = document.querySelector(
      `[data-shop-mega-trigger][aria-controls="${panel.id}"]`
    );
    return triggerEl ? pairFromTrigger(triggerEl) : null;
  };

  const resetFeaturedCard = (panel) => {
    const featured = panel?.querySelector('[data-ge-mega-featured]');
    if (!featured) return;
    const src = panel.getAttribute('data-ge-mega-default-image');
    const label = panel.getAttribute('data-ge-mega-default-label') || 'Shop all';
    const url = panel.getAttribute('data-ge-mega-default-url') || featured.getAttribute('href');
    if (url) featured.setAttribute('href', url);
    setFeaturedCard(panel, src, label);
  };

  const setFeaturedCard = (panel, src, label) => {
    const imgA = panel.querySelector('[data-ge-mega-featured-a]');
    const imgB = panel.querySelector('[data-ge-mega-featured-b]');
    const labelEl = panel.querySelector('[data-ge-mega-featured-label]');
    if (!imgA || !imgB || !src) {
      if (labelEl && label) labelEl.textContent = label;
      return;
    }
    const visible = imgA.classList.contains('is-visible') ? imgA : imgB;
    const hidden = visible === imgA ? imgB : imgA;
    if (visible.getAttribute('src') === src) {
      if (labelEl && label) labelEl.textContent = label;
      return;
    }
    hidden.removeAttribute('hidden');
    hidden.src = src;
    hidden.classList.add('is-visible');
    visible.classList.remove('is-visible');
    window.setTimeout(() => {
      if (!hidden.classList.contains('is-visible')) hidden.setAttribute('hidden', '');
    }, 220);
    if (labelEl && label) labelEl.textContent = label;
  };

  const bindFeaturedCard = (panel) => {
    if (!panel?.classList.contains('ge-mega--slideout') || panel.dataset.geMegaFeaturedBound) return;
    panel.dataset.geMegaFeaturedBound = 'true';
    resetFeaturedCard(panel);

    panel.querySelectorAll('[data-ge-mega-nav-link]').forEach((link) => {
      const onNavFocus = () => {
        const src = link.getAttribute('data-ge-mega-feature-image');
        const label = link.getAttribute('data-ge-mega-feature-label');
        if (src) setFeaturedCard(panel, src, label);
        else if (label) {
          const labelEl = panel.querySelector('[data-ge-mega-featured-label]');
          if (labelEl) labelEl.textContent = label;
        }
      };
      link.addEventListener('mouseenter', onNavFocus);
      link.addEventListener('focus', onNavFocus);
    });

    const nav = panel.querySelector('[data-ge-mega-primary-list]');
    const secondary = panel.querySelector('[data-ge-mega-secondary]');
    const showChildren = (link) => {
      panel.querySelectorAll('.ge-mega__primary-link').forEach((el) => el.classList.toggle('is-current', el === link));
      const idx = link?.getAttribute('data-ge-mega-index');
      panel.querySelectorAll('[data-ge-mega-children-for]').forEach((list) => {
        list.hidden = !idx || list.getAttribute('data-ge-mega-children-for') !== idx;
      });
    };
    panel.querySelectorAll('.ge-mega__primary-link').forEach((link) => {
      link.addEventListener('mouseenter', () => showChildren(link));
      link.addEventListener('focus', () => showChildren(link));
    });
    panel._geShowChildren = showChildren;
    nav?.addEventListener('mouseleave', (event) => {
      if (nav.contains(event.relatedTarget) || secondary?.contains(event.relatedTarget)) return;
      resetFeaturedCard(panel);
    });
    secondary?.addEventListener('mouseleave', (event) => {
      if (nav?.contains(event.relatedTarget) || secondary.contains(event.relatedTarget)) return;
      const current = panel.querySelector('.ge-mega__primary-link.is-current');
      if (current) current.dispatchEvent(new Event('focus'));
    });
  };

  const closeAll = () => {
    document.querySelectorAll('[data-shop-mega-panel]').forEach((panel) => {
      if (panel._geShowChildren) panel._geShowChildren(null);
      panel.classList.remove('is-open');
      panel.setAttribute('hidden', '');
      resetFeaturedCard(panel);
    });
    document.querySelectorAll('[data-shop-mega-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    document.body.classList.remove('ge-mega-open');
    const backdrop = backdropEl();
    if (backdrop) {
      backdrop.setAttribute('hidden', '');
      backdrop.classList.remove('is-open');
    }
    openPair = null;
  };

  const open = (pair) => {
    if (!pair) return;
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
    document.querySelectorAll('[data-shop-mega-panel]').forEach((panel) => {
      const on = panel === pair.panel;
      panel.classList.toggle('is-open', on);
      if (on) {
        const wasOpen = !panel.hasAttribute('hidden') && openPair?.panel === panel;
        panel.removeAttribute('hidden');
        bindFeaturedCard(panel);
        if (!wasOpen) resetFeaturedCard(panel);
      } else {
        panel.setAttribute('hidden', '');
        resetFeaturedCard(panel);
      }
    });
    document.querySelectorAll('[data-shop-mega-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', trigger === pair.trigger ? 'true' : 'false');
    });
    if (isDesktopOverlayPanel(pair.panel)) {
      window.dispatchEvent(new Event('scroll'));
      document.body.classList.add('ge-mega-open');
      const backdrop = backdropEl();
      if (backdrop) {
        backdrop.removeAttribute('hidden');
        backdrop.classList.add('is-open');
      }
    }
    openPair = pair;
  };

  const scheduleClose = () => {
    if (closeTimer) clearTimeout(closeTimer);
    closeTimer = setTimeout(closeAll, 160);
  };

  document.addEventListener(
    'mouseenter',
    (event) => {
      const pair = pairFromNode(event.target);
      if (pair) open(pair);
    },
    true
  );

  document.addEventListener(
    'mouseleave',
    (event) => {
      if (!openPair) return;
      const from = pairFromNode(event.target);
      if (!from || from.panel !== openPair.panel) return;
      const related = event.relatedTarget;
      if (related instanceof Node) {
        if (from.panel.contains(related) || from.trigger.contains(related)) return;
      }
      const next = pairFromNode(related);
      if (next && next.panel === from.panel) return;
      scheduleClose();
    },
    true
  );

  document.addEventListener('focusin', (event) => {
    const pair = pairFromNode(event.target);
    if (pair) {
      open(pair);
      return;
    }
    if (openPair) closeAll();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll();
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-shop-mega-backdrop]')) {
      closeAll();
      return;
    }

    if (!isHorizontalNav()) return;
    const trigger = target.closest('[data-shop-mega-trigger]');
    const pair = pairFromTrigger(trigger);
    if (!pair) return;
    if (!pair.panel.classList.contains('is-open')) {
      event.preventDefault();
      open(pair);
    }
  });
})();
